import React, { useEffect } from 'react';
import { useSelector } from 'react-redux';
import { IRootState } from 'src/reducers';
import selectors from 'src/selectors';
import Dialog from 'src/components/common/Dialog';
import Button from 'src/components/controls/forms/redesign/FormButton';
import { useForm } from 'react-hook-form';
import RHFInputBox from 'src/components/controls/tempRHFControls/RHFInputBox';
import DialogList from 'src/dialogs';

interface IEnterCheckNumberDialogProps {
    dialogName: string;
    handleReport?: (category: string, subcategory: string) => void;
    setCheckNumber?: (checkNumber: number) => void;
    setCheckDate?: (checkNumber: string) => void;
    hideDialog: () => void;
    showDialog: (dialogName: DialogList) => void;
    // When set, this is a regenerate (the voucher already has a check) rather than a first-time
    // write - Check Number and Check Date are seeded with these values and locked, since a
    // regenerate must re-use both as already persisted (mirrors ap_voucher.fmt's reprint trigger,
    // which never re-prompts for either - it only ever re-runs the report off the existing values).
    existingCheckNumber?: number;
    existingCheckDate?: string;
}

const CHECK_NUMBER_MAX_DIGITS = 10;

const EnterCheckNumberDialog = (props: IEnterCheckNumberDialogProps) => {
    const { register, errors, getValues, trigger, setError, clearErrors, setValue } = useForm();
    const isOpen = useSelector((state: IRootState) => selectors.layout.isDialogShowing(state, props.dialogName));

    // This component never unmounts between opens (Dialog's CSSTransition only removes its own
    // children's DOM), so a plain defaultVal would only ever apply on the very first open - a later
    // open for a different voucher (or switching between generate and regenerate) would keep
    // showing whatever was seeded the first time. Reseed explicitly instead.
    useEffect(() => {
        if (isOpen) {
            setValue('check.checkNumber', props.existingCheckNumber != null ? String(props.existingCheckNumber) : '');
            setValue('check.checkDate', props.existingCheckDate || new Date().toISOString().split('T')[0]);
            clearErrors();
        }
    }, [isOpen]);

    // AP_VOUCHER.CHK_NO is numeric(10,0) - a value beyond 10 digits would fail the eventual SQL
    // write, so this is caught here instead, in the same place a required-field error would show.
    const handleCheckNumberChange = (event: React.FormEvent<HTMLInputElement>) => {
        const input = event.currentTarget;
        if (input.value.length > CHECK_NUMBER_MAX_DIGITS) {
            input.value = input.value.slice(0, CHECK_NUMBER_MAX_DIGITS);
            setError('check.checkNumber', {
                type: 'manual',
                message: `Check Number cannot exceed ${CHECK_NUMBER_MAX_DIGITS} digits.`
            });
        } else {
            clearErrors('check.checkNumber');
        }
    };

    // RHF v6's reValidateMode ('onChange' by default) is supposed to auto-clear an existing error
    // on the next change, but that only fires reliably for user keystrokes on this uncontrolled
    // input - picking a date via the native date-picker widget doesn't consistently trigger it, so
    // the "required" error was staying up even after a valid date was selected. Clear it explicitly.
    const handleCheckDateChange = (event: React.FormEvent<HTMLInputElement>) => {
        if (!!event.currentTarget.value) {
            clearErrors('check.checkDate');
        }
    };

    const OKClicked = async () => {
        const isValid = await trigger();
        if (!isValid) {
            return;
        }
        let data = getValues();
        props.setCheckNumber(data.check.checkNumber);
        props.setCheckDate(data.check.checkDate);
        props.hideDialog();
        props.showDialog(DialogList.CREATE_CHECK_WARNING);
    };

    return (
        <Dialog
            title={props.existingCheckNumber != null ? 'Regenerate Check' : 'Enter Check Number'}
            dialogName={props.dialogName}
            isExitButtonInvisible={true}
            bodyStyle={{ width: '30%' }}
        >
            <div style={{ display: 'flex', justifyContent: 'center' }}>
                <form>
                    <RHFInputBox
                        label="Check Number"
                        labelOnLeft={true}
                        labelStyle={{ width: '15rem'}}
                        name={`check.checkNumber`}
                        type="number"
                        register={register}
                        errors={errors}
                        rules={{ required: 'Check Number is required.' }}
                        onChange={handleCheckNumberChange}
                        readOnly={props.existingCheckNumber != null}
                        style={{ width: '100%' }}
                    />
                    <RHFInputBox
                        label="Check Date"
                        labelOnLeft={true}
                        labelStyle={{ width: '15rem'}}
                        name={`check.checkDate`}
                        type="date"
                        register={register}
                        errors={errors}
                        rules={{ required: 'Check Date is required.' }}
                        onChange={handleCheckDateChange}
                        readOnly={props.existingCheckNumber != null}
                        style={{ width: '100%' }}
                    />
                </form>
            </div>
            <div className="form-row" style={{marginTop: '2rem', justifyContent: 'center'}}>
                <Button
                    className="blue-hover-button"
                    style={{ width: '24%', marginRight: '1rem' }}
                    text={'OK'}
                    onClick={() => OKClicked()}
                />
                <Button
                    className="blue-hover-button"
                    style={{ width: '24%', marginRight: '1rem' }}
                    text={'Cancel'}
                    onClick={() => props.hideDialog()}
                />
            </div>
        </Dialog>
    );
};

export default EnterCheckNumberDialog;



// WEBPACK FOOTER //
// ./src/components/accounts-payable/dialogs/EnterCheckNumberDialog.tsx